import { useState, useEffect } from "react";

const STORAGE_KEY = "android-install-prompt-dismissed";
const DISMISS_DURATION_DAYS = 7;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * Hook to handle Android PWA install prompt
 * Listens for beforeinstallprompt event and manages the deferred prompt
 */
function useAndroidInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      return;
    }

    // Check if user dismissed it recently
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    const isDismissedRecently =
      dismissedAt &&
      Date.now() - parseInt(dismissedAt) <
        DISMISS_DURATION_DAYS * 24 * 60 * 60 * 1000;

    if (isDismissedRecently) {
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome's default mini-infobar
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShouldShow(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Check if app was installed
    window.addEventListener("appinstalled", () => {
      setDeferredPrompt(null);
      setShouldShow(false);
    });

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;

    // Show the native install prompt
    await deferredPrompt.prompt();

    // Wait for user choice
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setDeferredPrompt(null);
      setShouldShow(false);
    }
  };

  const dismiss = (permanent: boolean = false) => {
    if (permanent) {
      localStorage.setItem(
        STORAGE_KEY,
        String(Date.now() + 365 * 24 * 60 * 60 * 1000 * 10)
      );
    } else {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    setShouldShow(false);
  };

  return { shouldShow, install, dismiss };
}

export function AndroidInstallPrompt() {
  const { shouldShow, install, dismiss } = useAndroidInstallPrompt();

  if (!shouldShow) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-40 animate-in fade-in slide-in-from-bottom-4 duration-300"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="mx-auto max-w-sm rounded-full border border-primary/30 bg-card-light/95 dark:bg-background-dark/95 backdrop-blur-lg pl-4 pr-2 py-2 shadow-lg">
        <div className="flex items-center gap-3">
          {/* App Icon */}
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-primary text-lg">
              install_mobile
            </span>
          </div>

          {/* Text */}
          <p className="text-content-light dark:text-white text-sm font-medium flex-1">
            Install Dasher
          </p>

          {/* Install button */}
          <button
            onClick={install}
            className="rounded-full bg-primary px-4 py-1.5 text-sm font-bold text-black"
          >
            Install
          </button>

          {/* Close button */}
          <button
            onClick={() => dismiss(false)}
            className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-slate-400 hover:text-slate-300 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      </div>
    </div>
  );
}
